import { Component } from '@theme/component';

/**
 * @typedef {Object} Refs
 * @extends {Component<Refs>}
 */
class TabGroup extends Component {
  /** @type {HTMLElement[]} */
  #tabs = [];
  #controller = new AbortController();

  /** Class toggled on the active tab. Overridable via the `active-class` attribute. */
  get #activeClass() {
    return this.getAttribute('active-class') || 'is-active';
  }

  /** Class toggled on the active panel. Falls back to the tab active class. */
  get #panelActiveClass() {
    return this.getAttribute('panel-active-class') || this.#activeClass;
  }

  /** Index of the currently active tab, or -1 if none is active yet. */
  get #activeIndex() {
    return this.#tabs.findIndex((tab) => tab.classList.contains(this.#activeClass));
  }

  connectedCallback() {
    super.connectedCallback();

    this.#tabs = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-tab]')));
    if (!this.#tabs.length) return;

    const { signal } = this.#controller;
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });

    for (const tab of this.#tabs) {
      if (!tab.hasAttribute('role')) tab.setAttribute('role', 'tab');
    }

    // Normalise aria/tabindex to match the server-rendered active tab.
    this.#activate(this.#activeIndex === -1 ? 0 : this.#activeIndex);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort(); // removes every listener in one call
  }

  /**
   * Resolve the panel for a tab: explicit `data-tab-target` first, else positional.
   * @param {number} index
   * @returns {Element | null}
   */
  #panelFor(index) {
    const target = this.#tabs[index]?.getAttribute('data-tab-target');
    if (target) return this.querySelector(target);
    return this.querySelectorAll('[data-tab-panel]')[index] ?? null;
  }

  /** @param {number} index */
  #activate(index) {
    const tabActive = this.#activeClass;
    const panelActive = this.#panelActiveClass;
    const panels = this.querySelectorAll('[data-tab-panel]');

    this.#tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.classList.toggle(tabActive, selected);
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });

    panels.forEach((panel) => panel.classList.remove(panelActive));
    this.#panelFor(index)?.classList.add(panelActive);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const tab = target.closest('[data-tab]');
    if (!(tab instanceof HTMLElement)) return;

    const index = this.#tabs.indexOf(tab);
    if (index !== -1) this.#activate(index);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    /** @type {Record<string, number>} */
    const directions = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    const direction = directions[event.key];
    if (!direction) return;

    event.preventDefault();
    const next = (this.#activeIndex + direction + this.#tabs.length) % this.#tabs.length;
    this.#activate(next);
    this.#tabs[next]?.focus();
  };
}

if (!customElements.get('tab-group')) {
  customElements.define('tab-group', TabGroup);
}

document.addEventListener('DOMContentLoaded', () => {
   if (window.matchMedia('(max-width: 767px)').matches) return;
	const stats = document.querySelectorAll('.nutrition-stats__stat');

	if (!stats.length) return;

	stats.forEach(stat => {
		const fruits = stat.querySelectorAll('.nutrition-stats__float-img');

		if (!fruits.length) return;

		stat.addEventListener('mousemove', (event) => {
			const rect = stat.getBoundingClientRect();

			// Mouse position relative to the center
			const mouseX = (event.clientX - rect.left) / rect.width - 0.5;
			const mouseY = (event.clientY - rect.top) / rect.height - 0.5;

			fruits.forEach((fruit, index) => {
				// Increased movement strength for each fruit
				const depth = (index + 1) * 12;

				const moveX = mouseX * depth;
				const moveY = mouseY * depth;

				fruit.style.setProperty('--move-x', `${moveX}px`);
				fruit.style.setProperty('--move-y', `${moveY}px`);
			});
		});

		// Reset fruits when mouse leaves the section
		stat.addEventListener('mouseleave', () => {
			fruits.forEach(fruit => {
				fruit.style.setProperty('--move-x', '0px');
				fruit.style.setProperty('--move-y', '0px');
			});
		});
	});
});